import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DataGrid,
  ErrorMessage,
  ListHeader,
  LoadingSpinner,
  useFormatting,
} from "@msys/ui";
import { ReportProblemOutlined as ReportProblemOutlinedIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  LinearProgress,
  LinearProgressProps,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ceil, floor } from "lodash-es";
import React from "react";
import { useParams } from "react-router-dom";
import {
  namedOperations,
  PimImportationStatus,
} from "../../../clients/graphqlTypes.js";
import { AddDatanormOnlineImportationButton } from "../../features/pimImportations/buttons/AddDatanormOnlineImportationButton.js";
import { AddPimImportationButton } from "../../features/pimImportations/buttons/AddPimImportationButton.js";
import { CancelPimImportationButton } from "../../features/pimImportations/buttons/CancelPimImportationButton.js";
import { DeletePimSupplierImportationsButton } from "../../features/pimImportations/buttons/DeletePimSupplierImportationsButton.js";
import { useSupplierPimImportationsQuery } from "./SupplierPimImportations.generated.js";

interface Props {}

export const SupplierPimImportations = ({ ...props }: Props) => {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const client = useApolloClient();
  const { getFormattedDate } = useFormatting();

  const { t } = useTranslate(["PimImportations", "ProductOverview", "Global"]);

  const pimImportationStatusLabels: Record<PimImportationStatus, string> = {
    to_index: t("indexing"),
    indexing: t("indexing"),
    indexed: t("indexed"),
    failed: t("failed"),
    has_duplicate: t("has_duplicate"),
    to_delete: t("deleting"),
    deleting: t("deleting"),
    deleted: t("deleted"),
  };

  const query = useSupplierPimImportationsQuery({
    client,
    fetchPolicy: "no-cache",
    variables: {
      supplierId,
    },
  });

  const pimImportations = getDataOrNull(
    query.data?.pimImportations
  )?.importations;

  const supplier = getDataOrNull(query.data?.pimSupplier)?.supplier;

  React.useEffect(() => {
    if (pimImportations?.some(importation => importation.isProcessing)) {
      query.startPolling(5000);
    } else {
      query.stopPolling();
    }

    return () => query.stopPolling();
  }, [pimImportations, query]);

  if (query.loading) return <LoadingSpinner />;
  if (query.error) return <ErrorMessage message={query.error.message} />;

  if (!supplier) return <ErrorMessage message="Supplier not found" />;

  const itekCompanyId = supplier.itekCompanyIdentifier;

  return (
    <>
      <ListHeader
        ActionButtons={
          <Stack
            direction="row"
            spacing={0.5}
            flexGrow={0}
            flexShrink={0}
            alignItems="center"
          >
            {itekCompanyId && (
              <AddDatanormOnlineImportationButton
                supplierId={supplierId}
                itekCompanyId={itekCompanyId}
                refetchQueries={[namedOperations.Query.SupplierPimImportations]}
              />
            )}
            <AddPimImportationButton
              supplierId={supplierId}
              refetchQueries={[namedOperations.Query.SupplierPimImportations]}
            />
            <DeletePimSupplierImportationsButton supplierId={supplierId} />
          </Stack>
        }
        marginBottom={0.5}
      />
      {pimImportations && (
        <DataGrid
          columns={[
            {
              field: "filename",
              headerName: t("Filename", { ns: "Global" }),
              flex: 1,
            },
            {
              field: "createdAt",
              headerName: t("Created at", { ns: "ProductOverview" }),
              flex: 1,
              valueFormatter(value) {
                return getFormattedDate(value);
              },
            },
            {
              field: "status",
              headerName: t("Status", { ns: "Global" }),
              flex: 1,
              renderCell: ({ row }) => {
                const warning = ["failed", "has_duplicate"].includes(
                  row.status
                );

                const isIndexing =
                  row.isProcessing &&
                  row.status !== "failed" &&
                  row.status !== "to_delete" &&
                  row.status !== "deleted" &&
                  row.status !== "deleting";

                return (
                  <Stack
                    color={"error"}
                    direction="row"
                    alignItems={"center"}
                    spacing={1}
                    flex={1}
                  >
                    <Chip
                      color={
                        warning
                          ? "error"
                          : isIndexing
                            ? "info"
                            : row.status === "indexed"
                              ? "success"
                              : row.status === "deleting" ||
                                  row.status === "to_delete" ||
                                  row.status === "deleted"
                                ? "warning"
                                : "default"
                      }
                      icon={warning ? <ReportProblemOutlinedIcon /> : undefined}
                      variant="filled"
                      size="small"
                      label={
                        isIndexing
                          ? pimImportationStatusLabels["indexing"]
                          : pimImportationStatusLabels[row.status]
                      }
                      sx={{
                        ".MuiChip-label": { textTransform: "capitalize" },
                      }}
                    />
                    {isIndexing ? (
                      <LinearProgressWithLabel
                        value={row.completionPercentage * 100}
                        sx={{ flex: 1 }}
                      />
                    ) : null}
                  </Stack>
                );
              },
            },
            {
              field: "totalProcessedProducts",
              headerName: t("Product count", { ns: "PimImportations" }),
              flex: 0.5,
              renderCell: ({ row }) => {
                return (
                  <Typography variant="body2">
                    {`${row?.totalProcessedProducts ?? 0} ${t("products")}`}
                  </Typography>
                );
              },
            },
            {
              field: "actions",
              headerName: "",
              flex: 0,
              renderCell: ({ row }) => {
                const warning = ["failed", "has_duplicate"].includes(
                  row.status
                );

                return !warning &&
                  row.isProcessing &&
                  row.status !== "to_delete" &&
                  row.status !== "deleting" ? (
                  <CancelPimImportationButton pimImportation={row} />
                ) : undefined;
              },
            },
          ]}
          rows={pimImportations}
          paginationMode="client"
        />
      )}
    </>
  );
};

function LinearProgressWithLabel(
  props: LinearProgressProps & { value: number }
) {
  return (
    <Box width="100%" flex={1} display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box width={35}>
        <Typography variant="body2" color="textSecondary">
          {`${
            floor(props.value) === 0 ? ceil(props.value) : floor(props.value)
          }%`}
        </Typography>
      </Box>
    </Box>
  );
}
